import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function PrivacyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#9B5DE5] to-[#EF476F] py-6 px-4">
        <div className="max-w-3xl mx-auto">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white/80 hover:text-white mb-4">
            <ArrowLeft size={20} /> Back
          </button>
          <h1 className="text-3xl font-black text-white" style={{ fontFamily: "Fredoka, sans-serif" }}>
            Privacy Policy
          </h1>
          <p className="text-white/80 mt-1">Last updated: March 5, 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto py-8 px-4">
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 space-y-6">
          
          <section>
            <h2 className="text-xl font-bold text-[#073B4C] mb-3">1. Introduction</h2>
            <p className="text-gray-600 leading-relaxed">
              Bible Buds ("we", "our", or "us") is committed to protecting the privacy of children and families. 
              This Privacy Policy explains how we collect, use, and protect information when you use our Service. 
              We comply with the Children's Online Privacy Protection Act (COPPA) and other applicable privacy laws.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#073B4C] mb-3">2. Information We Collect</h2>
            <p className="text-gray-600 leading-relaxed mb-3">
              We collect the following types of information:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li><strong>Parent Information:</strong> Name, email address, and payment information for account creation and subscription management.</li>
              <li><strong>Child Information:</strong> First name, age group, and lesson progress (collected with parental consent).</li>
              <li><strong>Usage Data:</strong> Lessons completed, badges earned, and app interaction data to improve the Service.</li>
              <li><strong>Device Information:</strong> Browser type and device identifiers for push notifications (optional).</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#073B4C] mb-3">3. How We Use Information</h2>
            <p className="text-gray-600 leading-relaxed mb-3">
              We use collected information to:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>Provide age-appropriate Bible lessons and activities</li>
              <li>Track learning progress and award badges</li>
              <li>Process subscription payments</li>
              <li>Send notifications about new lessons (with permission)</li>
              <li>Improve our Service and develop new features</li>
              <li>Respond to support requests</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#073B4C] mb-3">4. Children's Privacy (COPPA Compliance)</h2>
            <p className="text-gray-600 leading-relaxed">
              We take children's privacy seriously. We do not collect personal information from children under 13 
              without verifiable parental consent. Parents must create an account and add their children. 
              Children's accounts only store first names and learning progress - no contact information or 
              identifiable data is collected from children directly.
            </p>
            <p className="text-gray-600 leading-relaxed mt-3">
              <strong>Parental Rights:</strong> Parents can review, delete, or refuse further collection of their 
              child's information at any time by contacting us or through their account settings.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#073B4C] mb-3">5. Information Sharing</h2>
            <p className="text-gray-600 leading-relaxed mb-3">
              We do not sell or rent personal information. We may share information only in these cases:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li><strong>Church Communities:</strong> If you join a church group, your family name and lesson progress may be visible to church administrators.</li>
              <li><strong>Service Providers:</strong> We use trusted third parties (payment processors, hosting) who are bound by confidentiality agreements.</li>
              <li><strong>Legal Requirements:</strong> We may disclose information if required by law or to protect safety.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#073B4C] mb-3">6. Data Security</h2>
            <p className="text-gray-600 leading-relaxed">
              We implement industry-standard security measures to protect your information, including encryption, 
              secure servers, and access controls. However, no method of transmission over the Internet is 100% 
              secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#073B4C] mb-3">7. Data Retention</h2>
            <p className="text-gray-600 leading-relaxed">
              We retain personal information for as long as your account is active or as needed to provide the Service. 
              You can request deletion of your account and associated data at any time by contacting us.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#073B4C] mb-3">8. Cookies and Tracking</h2>
            <p className="text-gray-600 leading-relaxed">
              We use essential cookies to maintain your session and remember your preferences. We do not use 
              third-party advertising cookies or track children for advertising purposes.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#073B4C] mb-3">9. Your Rights</h2>
            <p className="text-gray-600 leading-relaxed mb-3">
              You have the right to:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>Access and review your personal information</li>
              <li>Correct inaccurate information</li>
              <li>Delete your account and data</li>
              <li>Opt out of marketing communications</li>
              <li>Withdraw consent for data collection</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#073B4C] mb-3">10. Changes to This Policy</h2>
            <p className="text-gray-600 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of significant changes 
              via email or through the Service. Your continued use after changes constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#073B4C] mb-3">11. Contact Us</h2>
            <p className="text-gray-600 leading-relaxed">
              If you have questions about this Privacy Policy or wish to exercise your privacy rights, contact us at:{" "}
              <a href="mailto:support@biblebuds.space" className="text-[#118AB2] hover:underline font-semibold">
                support@biblebuds.space
              </a>
            </p>
          </section>

        </div>
      </div>
    </div>
  );
}
